import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheckoutDate,
  getDurationPackageOptions,
  getLowestAvailableStayPayTier, OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { initialRooms } from 'lib/offer/occupancyUtils'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Plural from 'components/utils/Formatters/Plural'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useOffer from 'hooks/Offers/useOffer'
import PropertyRoomOption from './PropertyRoomOption'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPackageOptionsByRoom } from './singlePropertyUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(16)};
  }
`

interface Props {
  offer: App.HotelOffer;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  packageOptions: Array<OfferPackageOptionView>;
  roomOption?: OfferPageRoomOption;
  showFlightInformation?: boolean;
  destinationAirport?: App.Airport;
  onBookNowInvalid?: () => void;
  onViewPackageOptions: () => void;
  lowestAvailableStayPayTier?: App.StayPayTier | null;
}

function PropertyRoomOptions(props: Props) {
  const {
    offer,
    packageOptions,
    showFlightInformation,
    destinationAirport,
    roomOption,
    onViewPackageOptions,
    checkIn,
    checkOut,
    rooms = initialRooms,
    lowestAvailableStayPayTier,
  } = props

  const supportContact = useContext(ContactContext)
  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, bedbankPackageViews, mapping), [packageOptions, bedbankPackageViews, mapping])

  const highlightedPackage = useMemo(() => {
    if (hasRoomOption) {
      const highlightedView = packageOptionsByRoom.find(item => item.roomId === roomOption?.roomTypeId)
      return {
        roomId: roomOption?.roomTypeId,
        lpc: highlightedView?.lpc ?? [],
        bedbank: highlightedView?.bedbank ?? [],
      }
    }
  }, [hasRoomOption, packageOptionsByRoom, roomOption?.roomTypeId])

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageOptionsByRoom]) // regenerate id if list of rooms changes

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      {hasRateError && <MessageBanner
        kind="warning"
        tabletAlign="center"
        heading={`The third party booking system for ${offer.property?.name} is currently down`}
        description={`Please call our VIP team on ${supportContact.local.humanReadable} who are ready to provide assistance to complete your booking`}
      />}

    </LayoutContainer>
    {!hasRateError && <PackagesLayoutContainer>
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
          description={<>
            <b>Special offer:</b> Enjoy free nights for stays longer than <Plural count={lowestAvailableStayPayTier.stayNights} singular="day" withCount />. Select dates to check eligibility.
          </>}
        />}
        {hasRoomOption && !!highlightedPackage?.lpc.length && <PropertyRoomOption
          key={highlightedPackage.roomId}
          offer={offer}
          packageViews={highlightedPackage.lpc}
          bedbankViews={highlightedPackage.bedbank}
          flightDestination={destinationAirport}
          isHighlighted
          onViewPackageOptions={onViewPackageOptions}
          roomListId={roomListId}
        />}
        {packageOptionsByRoom.map((item) => {
          if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
            return null
          }
          return (<PropertyRoomOption
            key={item.roomId}
            offer={offer}
            packageViews={item.lpc}
            bedbankViews={item.bedbank}
            bedbankOffer={bedbankOffer}
            flightDestination={destinationAirport}
            onViewPackageOptions={onViewPackageOptions}
            fetchingBedbankViews={fetchingBedbankViews}
            roomListId={roomListId}
          />)
        })}
      </VerticalSpacer>
    </PackagesLayoutContainer>}
    {showFlightInformation && <LayoutContainer>
      <Caption variant="medium" align="center">
        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
      </Caption>
    </LayoutContainer>}
  </VerticalSpacer>
  )
}

type MappedProps = Pick<Props,
'offer' |
'checkIn' |
'checkOut' |
'rooms' |
'roomOption' |
'packageOptions' |
'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptions, preventRenderWithOpenFilter)
